import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  ArrowRight, 
  TrendingUp, 
  Shield, 
  Users, 
  Clock, 
  Award, 
  BarChart3,
  CheckCircle,
  PieChart,
  Target,
  Globe,
  Zap,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Star,
  Building,
  Briefcase,
  LineChart,
  Layers,
  Lock,
  TrendingDown,
  Calendar,
  DollarSign,
  Activity,
  Eye,
  Percent,
  Upload,
  FileText,
  Download
} from 'lucide-react';

// Regulatory Information Modal Component
const RegulatoryInformationModal = ({ showRegulatoryInfo, setShowRegulatoryInfo }) => {
  if (!showRegulatoryInfo) return null;

  return (
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h2 className="text-3xl font-bold text-black">Regulatory Information</h2>
          <button
            onClick={() => setShowRegulatoryInfo(false)}
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative z-10 text-black space-y-6">
          <div className="text-sm">
            <p className="text-gray-600 mb-4">Last Updated: 14/08/2025</p>
            
            <div className="space-y-6">
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Regulatory Status</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-blue-600 mb-2">Unregistered Platform:</h4>
                  <p>Ingenious Capital operates as an unregistered platform under exemptions available for communications with accredited and sophisticated investors.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-2">No Registration Required:</h4>
                  <p className="mb-2">We do not:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Provide investment advice requiring registration</li>
                    <li>Act as a broker-dealer or investment adviser</li>
                    <li>Offer or sell securities directly</li>
                    <li>Receive transaction-based compensation</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Exemption Reliance</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-teal-600 mb-2">Private Placement Exemptions:</h4>
                  <p className="mb-2">Investment opportunities discussed typically rely on:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Rule 506(b) and 506(c) private placement exemptions</li>
                    <li>Section 4(a)(2) private offering exemptions</li>
                    <li>Regulation D safe harbor provisions</li>
                    <li>Accredited investor limitations</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-green-400 mb-2">Platform Exemptions:</h4>
                  <p className="mb-2">Our operations rely on:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Publisher exclusion from investment adviser definition</li>
                    <li>Educational content and discussion facilitation</li>
                    <li>No investment advice or recommendations provided</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Compliance Framework</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-orange-400 mb-2">Accredited Investor Verification:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Documentation requirements for membership</li>
                    <li>Ongoing status verification procedures</li>
                    <li>Record-keeping and audit trails</li>
                    <li>Access restrictions for unqualified persons</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-yellow-400 mb-2">Anti-Fraud Compliance:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Prohibition on material misstatements</li>
                    <li>Disclosure of potential conflicts of interest</li>
                    <li>Member education on fraud prevention</li>
                    <li>Reporting procedures for suspicious activities</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-indigo-400 mb-2">Confidentiality Protections:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Non-disclosure agreements with members</li>
                    <li>Secure document handling procedures</li>
                    <li>Access controls and monitoring</li>
                    <li>Data protection measures</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Member Responsibilities</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-red-400 mb-2">Securities Law Compliance:</h4>
                  <p className="mb-2">Members must comply with applicable securities laws, including:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Accredited investor representations</li>
                    <li>Investment limits and restrictions</li>
                    <li>Resale and transfer limitations</li>
                    <li>Tax reporting obligations</li>
                  </ul>
                </div>
                
                <div className="border border-gray-300 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-blue-600 mb-2">Due Diligence:</h4>
                  <p className="mb-2">Members are solely responsible for:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Evaluating investment opportunities</li>
                    <li>Verifying sponsor credentials and track records</li>
                    <li>Understanding investment terms and risks</li>
                    <li>Making independent investment decisions</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Disclaimers and Limitations</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-amber-400 mb-2">No Regulatory Oversight:</h4>
                  <p>Investments discussed may not be subject to regulatory oversight or investor protections available for registered securities.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-pink-400 mb-2">No SIPC Protection:</h4>
                  <p>Private investments are not protected by SIPC or similar investor protection schemes.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-cyan-400 mb-2">Limited Recourse:</h4>
                  <p>Traditional investor remedies may not be available for private investment losses.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Potential Conflicts of Interest</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-orange-400 mb-2">Platform Revenue:</h4>
                  <p>We may receive fees from investment sponsors for platform access or services (when applicable).</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-violet-400 mb-2">Member Relationships:</h4>
                  <p>Platform operators or members may have business relationships with investment sponsors.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-rose-400 mb-2">Information Sharing:</h4>
                  <p>Discussions and materials may reflect biases or conflicts of the sharing parties.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Legal Disclaimer</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-red-400 mb-2">Professional Advice Required:</h4>
                  <p>These documents do not constitute legal advice. Consult with qualified securities counsel to ensure compliance with applicable laws and regulations.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-yellow-400 mb-2">Regulatory Changes:</h4>
                  <p>Securities laws are complex and subject to change. Members should stay informed of regulatory developments affecting their investments.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-green-400 mb-2">State Law Variations:</h4>
                  <p>Additional state law requirements may apply depending on member location and investment structures.</p>
                </div>
              </section>
              
              <section>
                <div className="border border-gray-300 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-purple-600 mb-3">Important Notice</h3>
                  <p className="text-black font-medium leading-relaxed">
                    This platform is designed exclusively for accredited investors and sophisticated investors. All materials and discussions are confidential and may not be shared with unauthorized parties. No investment advice is provided, and all investment decisions are made independently by members at their own risk.
                  </p>
                </div>
              </section>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6 text-center">
            <button
              onClick={() => setShowRegulatoryInfo(false)}
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300"
            >
              Close Regulatory Information
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Risk Disclosure Modal Component
const RiskDisclosureModal = ({ showRiskDisclosure, setShowRiskDisclosure }) => {
  if (!showRiskDisclosure) return null;

  return (
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h2 className="text-3xl font-bold text-black">Risk Disclosure</h2>
          <button
            onClick={() => setShowRiskDisclosure(false)}
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative z-10 text-black space-y-6">
          <div className="text-sm">
            <p className="text-gray-600 mb-4">Last Updated: 14/08/2025</p>
            
            <div className="space-y-6">
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Platform Risk Disclosure</h3>
                <p>This disclosure addresses risks associated with using our investment discussion platform and the types of investments typically discussed.</p>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">General Investment Risks</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-red-400 mb-2">High Risk Investments:</h4>
                  <p className="mb-2">Investments discussed on our platform typically involve:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Substantial risk of loss, including total loss of investment</li>
                    <li>Illiquidity and long holding periods</li>
                    <li>Limited or no regulatory oversight</li>
                    <li>Complex structures and strategies</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-orange-400 mb-2">Market Risk:</h4>
                  <p>All investments are subject to market volatility, economic conditions, and industry-specific factors.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-yellow-400 mb-2">Liquidity Risk:</h4>
                  <p>Private investments typically cannot be easily sold or transferred, and may have lengthy lock-up periods.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Private Investment Risks</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-teal-600 mb-2">Due Diligence Risk:</h4>
                  <p>Limited information may be available compared to public investments, requiring extensive independent analysis.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-blue-600 mb-2">Concentration Risk:</h4>
                  <p>Private investments often involve concentration in specific sectors, geographies, or strategies.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-indigo-400 mb-2">Operational Risk:</h4>
                  <p>Private investment managers may have limited operating history or resources.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-2">Regulatory Risk:</h4>
                  <p>Changes in securities laws may affect private investment structures and taxation.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Platform-Specific Risks</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-pink-400 mb-2">Information Risk:</h4>
                  <p>While we strive for accuracy, we do not verify all information shared on the platform.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-cyan-400 mb-2">Third-Party Risk:</h4>
                  <p>Information from investment sponsors and other members may be incomplete, inaccurate, or biased.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-green-400 mb-2">Access Risk:</h4>
                  <p>Technical issues may prevent access to time-sensitive investment opportunities.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-amber-400 mb-2">Confidentiality Risk:</h4>
                  <p>Breaches of confidentiality could affect investment opportunities or strategies.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">No Investment Advice</h3>
                
                <div className="border border-gray-300 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-medium text-red-600 mb-2">Independent Analysis Required:</h4>
                  <p>You must conduct your own due diligence and analysis of all investment opportunities.</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-blue-600 mb-2">Professional Consultation:</h4>
                  <p>Consider consulting with qualified tax, legal, and financial advisors before making investment decisions.</p>
                </div>
                
                <div className="border border-gray-300 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-orange-600 mb-2">No Recommendations:</h4>
                  <p>Our platform does not recommend or endorse any investments or investment strategies.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Acknowledgment</h3>
                <div className="border border-gray-300 rounded-lg p-4">
                  <p className="mb-2 font-medium text-black">By using our platform, you acknowledge that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You understand these risks and their potential impact</li>
                    <li>You have the financial capacity to bear investment losses</li>
                    <li>You will make independent investment decisions</li>
                    <li>You will not rely solely on information from our platform</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6 text-center">
            <button
              onClick={() => setShowRiskDisclosure(false)}
              className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300"
            >
              Close Risk Disclosure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms of Use Modal Component
const TermsOfUseModal = ({ showTermsOfUse, setShowTermsOfUse }) => {
  if (!showTermsOfUse) return null;

  return (
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h2 className="text-3xl font-bold text-black">Terms of Use</h2>
          <button
            onClick={() => setShowTermsOfUse(false)}
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative z-10 text-black space-y-6">
          <div className="text-sm">
            <p className="text-gray-600 mb-4">Last Updated: 14/08/2025</p>
            
            <div className="space-y-6">
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Acceptance of Terms</h3>
                <p>By accessing Ingenious Capital, you agree to these Terms of Use. Our platform is exclusively for accredited investors and sophisticated investors who meet eligibility requirements.</p>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Platform Description</h3>
                <p className="mb-2">Ingenious Capital provides a private platform where eligible investors can:</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Access investment opportunity discussions</li>
                  <li>Review offering materials and documentation</li>
                  <li>Participate in member discussions</li>
                  <li>Connect with investment sponsors</li>
                </ul>
                <div className="border border-gray-300 rounded-lg p-4">
                  <p className="font-semibold text-orange-600">IMPORTANT:</p>
                  <p>We do not provide investment advice, recommendations, or promote specific investments. All investment decisions are made independently by members.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Eligibility and Membership</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-teal-600 mb-2">Accredited Investor Requirements:</h4>
                  <p className="mb-2">You must certify that you meet one of the following criteria:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Individual income exceeding $200,000 ($300,000 joint) in each of the two most recent years</li>
                    <li>Net worth exceeding $1,000,000 (excluding primary residence)</li>
                    <li>Professional credentials (Series 7, 65, or 82 licenses)</li>
                    <li>Knowledgeable employee of private fund</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-orange-400 mb-2">Sophisticated Investor Requirements:</h4>
                  <p>You possess sufficient knowledge and experience in financial matters to evaluate investment risks and merits.</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-2">Verification:</h4>
                  <p>You must provide supporting documentation and maintain your accredited status throughout membership.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Platform Rules and Restrictions</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-green-400 mb-2">Permitted Activities:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Reviewing investment materials</li>
                    <li>Participating in educational discussions</li>
                    <li>Networking with other qualified investors</li>
                    <li>Accessing research and market information</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-red-400 mb-2">Prohibited Activities:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Sharing access credentials or materials with unqualified persons</li>
                    <li>Soliciting investments or providing investment advice</li>
                    <li>Copying or redistributing proprietary materials</li>
                    <li>Violating securities laws or regulations</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Investment Disclaimer</h3>
                <div className="border border-gray-300 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-medium text-yellow-600 mb-2">No Investment Advice:</h4>
                  <p className="mb-2">Information on our platform is for educational and informational purposes only. We do not:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Provide investment advice or recommendations</li>
                    <li>Endorse or promote specific investments</li>
                    <li>Act as investment advisers or broker-dealers</li>
                    <li>Receive compensation from investment sponsors</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-blue-600 mb-2">Independent Decisions:</h4>
                  <p>All investment decisions are made solely by you based on your own analysis and judgment.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Confidentiality</h3>
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-indigo-400 mb-2">Material Confidentiality:</h4>
                  <p>All information accessed through the platform is confidential and may not be shared with unauthorized parties.</p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-pink-400 mb-2">Non-Disclosure:</h4>
                  <p>Members agree to maintain confidentiality of all proprietary information, investment discussions, and member identities.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Liability and Risk</h3>
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-cyan-400 mb-2">Platform Disclaimer:</h4>
                  <p className="mb-2">We provide the platform "as is" without warranties of any kind. We are not liable for:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Investment losses or gains</li>
                    <li>Accuracy of third-party information</li>
                    <li>Technical platform issues</li>
                    <li>Actions of other members or sponsors</li>
                  </ul>
                </div>
                <div className="border border-gray-300 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-red-600 mb-2">Investment Risk:</h4>
                  <p>You acknowledge that investments discussed on the platform involve substantial risk and may result in complete loss of capital.</p>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Termination</h3>
                <p>We may terminate membership for violations of these terms, loss of accredited status, or breach of confidentiality obligations.</p>
              </section>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6 text-center">
            <button
              onClick={() => setShowTermsOfUse(false)}
              className="bg-gradient-to-r from-orange-500 to-teal-500 hover:from-orange-600 hover:to-teal-600 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300"
            >
              Close Terms of Use
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Notice Modal Component
const PrivacyNoticeModal = ({ showPrivacyNotice, setShowPrivacyNotice }) => {
  if (!showPrivacyNotice) return null;

  return (
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h2 className="text-3xl font-bold text-black">Privacy Notice</h2>
          <button
            onClick={() => setShowPrivacyNotice(false)}
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative z-10 text-black space-y-6">
          <div className="text-sm">
            <p className="text-gray-600 mb-4">Last Updated: 14/08/2025</p>
            
            <div className="space-y-6">
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Introduction</h3>
                <p>Ingenious Capital ("we," "us," or "our") operates as a private investment discussion platform. This Privacy Notice explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Information We Collect</h3>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-teal-600 mb-2">Personal Information:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Name, address, email address, phone number</li>
                    <li>Accredited investor status and supporting documentation</li>
                    <li>Professional background and investment experience</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-orange-600 mb-2">Technical Information:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>IP address, browser type, operating system</li>
                    <li>Device identifiers and session data</li>
                    <li>Platform usage patterns</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-purple-600 mb-2">Investment-Related Information:</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Investment interests and preferences</li>
                    <li>Discussion participation and contributions</li>
                    <li>Document access history</li>
                  </ul>
                </div>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">How We Use Your Information</h3>
                <p className="mb-2">We use your information to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Verify accredited/sophisticated investor status</li>
                  <li>Facilitate access to investment discussions and materials</li>
                  <li>Maintain platform security and prevent unauthorized access</li>
                  <li>Communicate platform updates and relevant information</li>
                  <li>Improve user experience and platform functionality</li>
                  <li>Comply with applicable legal obligations</li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Information Sharing and Disclosure</h3>
                <p className="mb-2">We may share your information with:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Investment sponsors and deal originators (with your consent)</li>
                  <li>Service providers supporting platform operations</li>
                  <li>Legal authorities when required by law</li>
                  <li>Other platform members in discussion forums (limited information only)</li>
                </ul>
                <p className="mt-3 font-medium text-teal-600">We do not sell your personal information to third parties.</p>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Data Security</h3>
                <p className="mb-2">We implement security measures including:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Encrypted data transmission and storage</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Regular security monitoring</li>
                  <li>Confidentiality agreements with service providers</li>
                </ul>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Data Retention</h3>
                <p>We retain your information for as long as your membership remains active and as required by applicable law.</p>
              </section>
              
              <section>
                <h3 className="text-xl font-semibold text-black mb-3">Your Rights</h3>
                <p className="mb-2">You may:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Access and review your personal information</li>
                  <li>Request corrections to inaccurate data</li>
                  <li>Request account deactivation</li>
                  <li>Opt-out of non-essential communications</li>
                </ul>
              </section>
            </div>
          </div>
          
          <div className="border-t border-gray-300 pt-6 text-center">
            <button
              onClick={() => setShowPrivacyNotice(false)}
              className="bg-gradient-to-r from-teal-500 to-orange-500 hover:from-teal-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300"
            >
              Close Privacy Notice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
const BookingCalendar = ({ showBookingCalendar, setShowBookingCalendar }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    selectedDate: '',
    selectedTime: ''
  });
  
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const blockedDates = [
    '2025-01-15', '2025-01-16', '2025-01-22', '2025-01-23'
  ];
  
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
  ];

  // Simple input handler
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch booked slots
  useEffect(() => {
    if (formData.selectedDate) {
      fetchBookedSlots(formData.selectedDate);
    }
  }, [formData.selectedDate]);

  const fetchBookedSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const backendUrl = import.meta.env?.REACT_APP_BACKEND_URL || process.env?.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/booked-slots/${date}`);
      
      if (response.ok) {
        const data = await response.json();
        setBookedSlots(data.booked_times || []);
      } else {
        setBookedSlots([]);
      }
    } catch (error) {
      console.error('Error fetching booked slots:', error);
      setBookedSlots([]);
    }
    setLoadingSlots(false);
  };

  // Generate available dates
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        const dateStr = date.toISOString().split('T')[0];
        if (!blockedDates.includes(dateStr)) {
          dates.push({
            date: dateStr,
            display: date.toLocaleDateString('en-GB', { 
              weekday: 'short', 
              day: 'numeric', 
              month: 'short' 
            })
          });
        }
      }
    }
    return dates;
  };

  const availableDates = generateAvailableDates();
  const availableTimeSlots = timeSlots.filter(time => !bookedSlots.includes(time));

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.selectedDate || !formData.selectedTime) {
      alert('Please fill in all required fields and select date/time');
      return;
    }
    
    try {
      const backendUrl = import.meta.env?.REACT_APP_BACKEND_URL || process.env?.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/send-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company || '',
          date: formData.selectedDate,
          time: formData.selectedTime
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Reset and close
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        selectedDate: '',
        selectedTime: ''
      });
      setShowBookingCalendar(false);
      
      alert(result.message || 'Booking request sent successfully! You will be contacted shortly to confirm your appointment.');
      
    } catch (error) {
      console.error('Error sending booking request:', error);
      
      // Fallback to mailto
      const bookingDetails = `Consultation Booking Request:

CLIENT DETAILS:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Company: ${formData.company || 'N/A'}

APPOINTMENT DETAILS:
Date: ${new Date(formData.selectedDate).toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })}
Time: ${formData.selectedTime}

Please confirm this appointment or suggest an alternative if this time is not available.

Best regards,
Ingenious Capital Booking System`;
      
      const mailtoLink = `mailto:appointmentsingcap@gmail.com?subject=Consultation Booking Request - ${formData.name}&body=${encodeURIComponent(bookingDetails)}`;
      window.location.href = mailtoLink;
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        selectedDate: '',
        selectedTime: ''
      });
      setShowBookingCalendar(false);
      
      alert('Booking request prepared! Your email client will now open with the consultation booking details pre-filled. Please send the email to complete your booking.');
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      selectedDate: '',
      selectedTime: ''
    });
  };

  if (!showBookingCalendar) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-5xl w-full max-h-[95vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h3 className="text-2xl font-bold text-gray-900">Schedule Your Consultation</h3>
          <button
            onClick={() => setShowBookingCalendar(false)}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information Form */}
          <div className="relative z-10">
            <h4 className="text-lg font-semibold text-gray-900 mb-6">Your Details</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">Full Name *</label>
                <input 
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full p-4 bg-white border border-gray-300 rounded-xl text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">Email Address *</label>
                <input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full p-4 bg-white border border-gray-300 rounded-xl text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">Phone Number *</label>
                <input 
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full p-4 bg-white border border-gray-300 rounded-xl text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="+44 20 1234 5678"
                />
              </div>
              
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">Company (Optional)</label>
                <input 
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full p-4 bg-white border border-gray-300 rounded-xl text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                  placeholder="Your company name"
                />
              </div>
            </div>
          </div>
          
          {/* Date & Time Selection */}
          <div>
            {/* Date Selection */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Select Date</h4>
              <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                {availableDates.slice(0, 12).map((dateObj) => (
                  <button
                    key={dateObj.date}
                    onClick={() => handleChange('selectedDate', dateObj.date)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                      formData.selectedDate === dateObj.date
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {dateObj.display}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Time Selection */}
            {formData.selectedDate && (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Select Time
                  {loadingSlots && (
                    <span className="ml-2 text-sm text-gray-600">(Loading available slots...)</span>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                  {availableTimeSlots.length > 0 ? (
                    availableTimeSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => handleChange('selectedTime', time)}
                        disabled={loadingSlots}
                        className={`p-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                          formData.selectedTime === time
                            ? 'bg-orange-500 text-white'
                            : loadingSlots
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        {time}
                      </button>
                    ))
                  ) : loadingSlots ? (
                    <div className="col-span-2 text-center text-gray-600 py-4">
                      Loading available time slots...
                    </div>
                  ) : (
                    <div className="col-span-2 text-center text-gray-600 py-4">
                      No available slots for this date. Please select another date.
                    </div>
                  )}
                </div>
                
                {bookedSlots.length > 0 && !loadingSlots && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-800">
                      <strong>Unavailable times:</strong> {bookedSlots.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Booking Summary & Submit */}
        {formData.selectedDate && formData.selectedTime && formData.name && formData.email && formData.phone && (
          <div className="mt-8 bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-800 mb-2"><strong>Name:</strong> {formData.name}</p>
                <p className="text-gray-800 mb-2"><strong>Email:</strong> {formData.email}</p>
                <p className="text-gray-800 mb-2"><strong>Phone:</strong> {formData.phone}</p>
                {formData.company && <p className="text-gray-800"><strong>Company:</strong> {formData.company}</p>}
              </div>
              <div>
                <p className="text-gray-800 mb-2">
                  <strong>Date:</strong> {new Date(formData.selectedDate).toLocaleDateString('en-GB', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-gray-800 mb-4">
                  <strong>Time:</strong> {formData.selectedTime}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-xl font-medium transition-all duration-300"
              >
                Send Booking Request
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <Button
                onClick={handleReset}
                variant="outline"
                className="px-6 py-4 border-gray-300 text-gray-800 hover:bg-gray-100 rounded-xl"
              >
                Reset All
              </Button>
            </div>
          </div>
        )}
        
        <div className="mt-6 text-center">
          <p className="text-base text-gray-800">
            Available Monday - Friday, 9:00 AM - 5:00 PM GMT • All fields marked with * are required
          </p>
        </div>
      </div>
    </div>
  );
};

// Investment Detail Modal Component
const InvestmentDetailModal = ({ investment, onClose }) => {
  if (!investment) return null;

  return (
    <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-2xl relative">
        
        <div className="flex justify-between items-center mb-8 relative z-10">
          <h2 className="text-3xl font-bold text-black">{investment.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="relative z-10 text-black space-y-6">
          <div className="mb-6">
            <p className="text-lg text-gray-600 mb-4">{investment.category}</p>
            <img 
              src={investment.image} 
              alt={investment.name}
              className="w-full max-w-md mx-auto rounded-lg border border-gray-200 mb-6"
            />
          </div>

          <div className="space-y-4">
            <div className="text-gray-800 leading-relaxed space-y-4">
              {investment.description.split('\n\n').map((paragraph, idx) => (
                <p key={idx} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-base font-medium text-gray-900">Investment Stage: </span>
                <span className="text-base text-gray-800">{investment.stage}</span>
              </div>
              <div>
                <span className="text-base font-medium text-gray-900">Target Return: </span>
                <span className="text-base text-gray-800">{investment.targetReturn}</span>
              </div>
            </div>
            
            {investment.fundingInfo && (
              <div className="mb-4">
                <div className="bg-white border border-gray-300 rounded-lg p-4">
                  <span className="text-base font-medium text-gray-900">Funding Progress: </span>
                  <span className="text-base text-gray-800">{investment.fundingInfo}</span>
                </div>
              </div>
            )}
            
            {investment.pitchDeckUrl && investment.pitchDeckUrl !== '#' && (
              <button
                onClick={() => window.open(investment.pitchDeckUrl, '_blank')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300"
              >
                View Pitch Deck
              </button>
            )}
          </div>
          
          <div className="border-t border-gray-300 pt-6 text-center">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const IngeniousCapital = () => {
  const [currentPage, setCurrentPage] = useState('logo');
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [showRiskDisclosure, setShowRiskDisclosure] = useState(false);
  const [showRegulatoryInfo, setShowRegulatoryInfo] = useState(false);
  const [expandedInvestmentSection, setExpandedInvestmentSection] = useState(null);
  const [selectedInvestment, setSelectedInvestment] = useState(null);

  // Investment process content
  const investmentProcessContent = {
    'due-diligence': {
      title: 'The Due Diligence Process',
      content: `For every opportunity, Ingenious Capital issues a Pitch Page or a Pitch Deck containing the core proposition, information about the business and an important disclosure document, which we call the Summary of Key Information (you will receive the Summary of Key Information via email). All investors must read the Pitch Page and the Summary of Key Information, because they contain important due diligence information.

You must also consider your own personal investment requirements and consider making your own further due diligence enquiries, which you can do by contacting us via Whatsapp or email to ask the company questions about the business and/or investment proposition. Ingenious Capital Ltd provides detailed information and analysis on each investment opportunity to help you make informed decisions. However, we do not offer personalized financial advice. We recommend consulting with a qualified financial advisor to determine which investments align best with your financial goals and risk tolerance.`
    },
    'share-price': {
      title: 'How is the share price calculated?',
      content: `The share price for each investment is calculated based on a thorough evaluation of the company's current financial status, market conditions, and growth potential. This includes an assessment of the company's assets, liabilities, revenue, and future earning prospects. The share price reflects the fair market value of the company's equity at the time of the offering.`
    },
    'company-valuation': {
      title: 'How is the valuation of a company calculated?',
      content: `The valuation of a company on Ingenious Capital Ltd is determined using various methodologies, depending on the nature of the business and the available financial data. Common methods include:

• Discounted Cash Flow (DCF) Analysis: Projects future cash flows and discounts them back to present value.
• Comparable Company Analysis: Evaluates the valuation of similar companies in the same industry.
• Precedent Transactions Analysis: Looks at past transactions of similar companies.
• Asset-Based Valuation: Assesses the value of the company's assets and liabilities.

Our team of experts conducts a rigorous due diligence process to ensure that the valuation is accurate and reflects the true worth of the company.`
    },
    'making-investment': {
      title: 'Making Your Investment',
      content: `The minimum amount you can invest in a pitch on Ingenious Capital Ltd varies depending on the specific investment opportunity. Generally, the minimum investment amount is designed to be accessible, allowing a wide range of investors to participate. Specific details for each investment will be identified in the relevant Pitch Page.

From here you will complete an application form either as an individual, with a partner or as a company, stating the amount you wish to invest and confirming your HNW or Sophisticated Investor status. You are also required to submit identity documents as part of the KYC process and confirm the source of your funds to meet anti money laundering requirements.`
    },
    'after-investment': {
      title: 'What happens after you\'ve invested?',
      content: `After you've made an investment, your funds will be allocated to the chosen opportunity, and you will become an equity holder in the respective company or project. You will receive a confirmation email detailing the specifics of your investment. This will provide you with your own log in to your investor portal where you can track the progress of your investment through your investment account, and you will receive regular updates from the businesses you've invested in.

These updates may include financial reports, progress on business milestones, strategic developments, and other relevant information - Ingenious Capital ensures that investors are kept informed through periodic emails and updates on your account dashboard.

Once you receive your ownership statement, you will have official documentation confirming your stake in the investment. This statement will outline the number of shares or units you hold, the value of your investment, and any relevant terms or conditions. You should review this document carefully and keep it for your records.`
    },
    'investment-returns': {
      title: 'Making a Return on Your Investment',
      content: `How do I make money from my investment?

Your returns will depend on the type of investment you choose. Broadly, there are two ways to benefit: income along the way and growth when you exit. Examples include:

Dividends (Shares & ETFs): Regular payments from company profits or fund distributions.
Capital Growth (Shares, ETFs & Property): Selling your investment for more than you paid, either through the stock market, property appreciation, or company sale.
Interest (Loans & Bonds): Fixed payments over the life of the loan, plus your original capital back at the end.
Rental Income (Property): Steady income from tenants, alongside the potential for the property to rise in value.
Exit Events (Private Companies): A sale, merger, or stock market listing (IPO) can create a significant opportunity for investors to cash out at a profit.

What are my exit options?

Different investments give you different ways to realise your gains:

Shares & ETFs:
Listed investments can usually be sold on the market at any time, providing flexibility.
Private company shares are less liquid but can often be sold during funding rounds, buy-backs, or when the company is sold or goes public.

Property:
Investors may choose to sell once values have risen or development is complete.
Some strategies allow refinancing to release capital while still holding the property.
Rental income can provide steady cash flow until you decide to exit.

Loans:
Most loans pay interest regularly and return your full capital at maturity.
Some may allow for early repayment or refinancing, giving you your money back sooner.

Could I ever be required to sell?

Generally, you stay in control of when you exit. However, there are rare situations where you might be required to sell:

In private companies, majority shareholder decisions (such as a company sale) can sometimes oblige all shareholders to sell under the same terms.
In property partnerships or funds, a collective decision may lead to the sale of the property.
In loan agreements, early repayment by the borrower may mean your capital is returned sooner than expected.`
    }
  };

  // Investment opportunities data
  const investmentOpportunities = {
    'checkaface': {
      name: 'Checkaface',
      category: 'FinTech · Series A · EIS/SEIS',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/9j3u1j5i_Image%2034.jpeg',
      description: `The United Kingdom stands at a turning point in digital identity. For decades, governments have flirted with centralised ID systems. Every attempt has collapsed under the weight of public opposition. Today, more than 80% of the British public reject the concept of a government-owned identity database, fearing a "papers, please" society where every movement is monitored and every detail controlled.

Yet the demand for trusted, secure identity has never been greater. Employers face legal obligations to conduct Right to Work checks. Landlords must confirm Right to Rent. Banks and fintechs wrestle with fraud and money laundering under stricter KYC and AML regulations. Online platforms — from gaming to dating — are compelled by the Online Safety Act to prove the ages of their users.

Fraud and impersonation cost the UK economy billions every year. Citizens are increasingly vulnerable to scams, identity theft, and deepfake attacks. Businesses face mounting compliance costs. The state's centralised answer has failed.

Into this gap steps Checkaface Ltd, with a proposition both radical and inevitable:

• A citizen-owned digital identity, not controlled by government or corporation.
• A self-sovereign wallet, where personal data lives only on the individual's device, never in a centralised database.
• Quad-Secure biometric technology, combining face, voice, retina, and tongue recognition with certified anti-deepfake detection.
• A business model where the service is free to citizens, but enterprises and governments pay to access trust.

Checkaface is not just another app. It is the foundation of a new digital identity infrastructure for the UK. An infrastructure that can save government billions, reduce fraud across industries, protect consumers, and scale internationally into a $100bn market.

<strong>Checkaface has received HMRC Advance Assurance Approval for SEIS/EIS, making it a very attractive tax efficient investment.</strong>`,
      stage: 'Series A',
      targetReturn: '200% IRR over a 3 year investment cycle',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/jdnh5xnj_Checkaface-Ltd%20Pitch.pdf',
      fundingInfo: 'Target Raise: £5m - Raised so far £2.3m'
    },
    'section106': {
      name: 'Section 106 Reclaims',
      category: 'Alternative Assets · Property Law',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/opfe9cuy_Image%2021.jpeg',
      description: `Section 106 Reclaims presents a specialized property investment opportunity focused on reclaiming developer contributions from local authorities. This unique asset class offers investors exposure to recoverable funds with strong legal foundations.

The opportunity leverages extensive legal expertise and established relationships with local councils to identify and recover significant sums. Historical success rates exceed 85% with average recovery periods of 6-12 months.

Key benefits include: minimal correlation to traditional property cycles, predictable returns, and backed by comprehensive legal precedent in UK property law.`,
      stage: 'Alternative Assets',
      targetReturn: '20-25% IRR over 6-12 months',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/hne5nb5g_Section-106-Reclaim-Investment-3.pdf',
      fundingInfo: 'Target Raise: £5m - Raised so far £3.1m'
    },
    'serenity-lakes': {
      name: 'Serenity Lakes',
      category: 'Property Development · Secured',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/8tj1dj84_Pic%201.jpg',
      description: `Serenity Lakes is a perfect example of how we invest in property. The owners have an excellent track record in leisure development and are seeking funding to assist in the construction of the development. Rather than lend money and be exposed to the development risk Ingenious Capital has negotiated to purchase 25 of the completed units at a 25% discount to value.

The site is currently going through the final stages of planning with Maidstone Council and the Environment Agency, with all correspondence demonstrating high level support for the scheme.

Ingenious will exchange conditionally on the units with a 25% deposit, with a deferred completion set for 6 months after the final unit is completed, giving us time to sell or arrange finance to complete the purchase. Conditional exchange removes all the risk for us but enables the developers to arrange a higher level of finance from their bank to fund the development, making it a win-win situation.

As the development progresses the value of the units rises and accordingly it provides us with an opportunity to sell our position on in a 6 month period or stay in for the full 2 years to optimise value. The estimated return over 6 months is in the region of 15-20% and over 2 years is likely in excess of 100%.

In the event something goes wrong, our funds are protected in escrow where we have negotiated a 5% rate of interest, so the downside in the unlikely event of the site not being completed is a guaranteed 5% return, while the upside is a 100%+ return over 2 years.`,
      stage: 'Property Development',
      targetReturn: '6 months: 15-20% | 2 years: 100%+ | Minimum: 5%',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_ingcap-reset/artifacts/fsk4m607_915-WWP_Serenity%20Lakes-Design%20Update-220819_Low%20Res.pdf',
      fundingInfo: 'Target Raise: £2.5m - Raised so far £1.3m'
    },
    'carbonguard': {
      name: 'CarbonGuard Ltd',
      category: 'Climate Tech · Direct Air Capture · EIS/SEIS',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/pnuta9eb_Image%2040.jpeg',
      description: `CarbonGuard Ltd - First-mover in UK Direct Air Capture (DAC) integrated into motorway barriers & lampposts. Dual revenues: hardware sales + recurring biofuel & carbon credit income.

<strong>Market Opportunity:</strong>
Scalable addressable market: UK £2.8bn; global replication potential with EIS Advance Assurance secured.

<strong>Tax Benefits:</strong>
30% Income Tax Relief, CGT exemption on gains. £12m raise = £3.6m immediate tax relief.

<strong>Funding & Grants:</strong>
£8.5m+ in non-dilutive grants identified from Innovate UK, Horizon Europe, Climate-KIC. Reduces early-stage investor capital at risk by 40%+.

<strong>Financial Projections:</strong>
Break-even end year 2, EBITDA rising to £27.7m by Year 7.

<strong>Exit Valuations (Year 7):</strong>
• Conservative: £166m – £221m (6–8× EBITDA)
• Strategic: £277m – £416m (10–15× EBITDA) 
• Licensing/IP: £554m – £693m+ (20–25× EBITDA)

<strong>Target Returns:</strong> 5–10× CoC | IRR 27–40%

Blockchain-verified credits ensure transparent, tradable carbon credits. Public benefit includes cleaner air and £3bn+ NHS savings from pollution reduction, perfectly aligned with UK Net Zero + ETS policy.`,
      stage: 'Climate Tech Series A',
      targetReturn: 'Target Returns: 5–10× CoC | IRR 27–40%',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/kyv52e5s_Transforming-Infrastructure-into-Climate-Solutions-3.pdf',
      fundingInfo: 'Target Raise: £5m - Raised so far £4.1m'
    },
    'cryptoflow': {
      name: 'CryptoFlow',
      category: 'FinTech · Pre Seed · EIS/SEIS',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/o34ngzv9_Image%2003-09-2025%20at%2011.08.jpeg',
      description: `Pre Seed Opportunity – CryptoFlow is seeking a £1 million raise to support its seed funding round. This comes with significant SEIS Tax Benefits and an excellent return exceeding 50% in under 12 months.

<strong>The Core Proposition</strong>

Institutional investors (family offices, funds, corporates) already trade digital assets, but their tools for custody, trading, accounting, and compliance are fragmented and don't integrate. CryptoFlow solves this by acting as the "operating system" that unifies these tools, letting institutions manage all digital asset operations on one platform.

<strong>Why CryptoFlow Is a Compelling Investment</strong>

<strong>Lower Risk:</strong> CryptoFlow doesn't take trading risk or hold client assets. It's a software subscription model, connecting trusted providers—like "picks-and-shovels" for the digital asset industry.

<strong>Recurring Revenue:</strong> Institutions pay monthly subscriptions (£10k–£75k+ per client) for essential software, creating stable, expandable income.

<strong>High Margins:</strong> As a pure software business, most new revenue is profit after the platform is built.

<strong>Sticky Customers:</strong> Deep integration into clients' operations makes switching difficult, ensuring high retention.

<strong>Regulatory Tailwinds:</strong> As regulations tighten, demand grows for automated compliance and reporting—CryptoFlow's core strengths.

<strong>Multiple Exit Options:</strong> Strategic buyers include custodians, trading infrastructure, fund administrators, and audit firms. Both acquisition and IPO are viable.

<strong>What CryptoFlow Offers</strong>

<strong>Command Center:</strong> One dashboard for all positions, prices, and risks.

<strong>Treasury:</strong> Automates asset movement to optimize yield, fees, and timing.

<strong>Ops & Compliance:</strong> Automates audit trails, flags suspicious activity, and generates regulatory reports.

<strong>Why Now?</strong>

Institutions want digital asset exposure, but operations are messy and regulations are increasing. The winners will make digital assets safe, connected, and auditable—CryptoFlow aims to be that essential infrastructure.`,
      stage: 'Pre Seed',
      targetReturn: '50%+ in under 12 months',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/cixlm93x_CryptoFlow%20Pitchdeck%200925.pdf',
      fundingInfo: 'Target Raise: £1m - Raised so far £0.45m'
    },
    'trackd': {
      name: 'Track\'d',
      category: 'Fleet Tech · Series A · EIS/SEIS',
      image: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/k19xws3v_image.png',
      description: `Trackd is a UK company commercialising a hardware‑agnostic (bring‑your‑own‑device) fleet telematics platform that lets operators change software without replacing any installed trackers. That single design choice removes the largest source of friction in our market—vendor lock‑in and capex—and it turns painful 'rip‑and‑replace' programmes into a simple software switch.

We pair this with a compliance‑first build for the UK and Europe—local data residency, GDPR/worker‑monitoring best practice, and an ISO 27001 programme—so buyers can adopt Trackd quickly and safely.

<strong>Our vision</strong> is a fleet ecosystem where software choice is not constrained by hardware.

<strong>Our mission</strong> is to make telematics simple, standards‑driven, and trustworthy: a platform that fleets can adopt in days, not months; that respects driver privacy; and that delivers measurable savings in fuel, insurance, safety and compliance.`,
      stage: 'Series A',
      targetReturn: 'Competitive fleet tech returns',
      pitchDeckUrl: 'https://customer-assets.emergentagent.com/job_wealth-platform-4/artifacts/cgr77hlu_Track-d%20Pitchdeck.pdf',
      fundingInfo: 'Target Raise: £2.5m - Raised so far £2.05m'
    }
  };

  // Prevent body scroll when on logo page
  useEffect(() => {
    if (currentPage === 'logo') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [currentPage]);

  const navigateToPage = (page) => {
    setCurrentPage(page);
  };

  const scrollToSection = (sectionId) => {
    setCurrentPage('site');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Calculate offset for fixed navigation (80px height)
        const navHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    
    useEffect(() => {
      if (!hasAnimated) {
        const timer = setTimeout(() => {
          setHasAnimated(true);
          let current = 0;
          const increment = end / 50;
          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 30);
          return () => clearInterval(timer);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [end, hasAnimated]);
    
    return <span>{prefix}{count}{suffix}</span>;
  };

  // Logo Page Component
  const LogoPage = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    // Debug log to confirm this version is loading
    console.log("NEW LOGO PAGE WITH VIDEO LOADING");
    
    useEffect(() => {
      // Detect mobile device
      const checkIfMobile = () => {
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setIsMobile(isMobileDevice);
        console.log("Is mobile device:", isMobileDevice);
      };
      
      checkIfMobile();
    }, []);
    
    return (
      <div className="h-screen w-screen overflow-hidden relative flex flex-col">
        {/* Background - Video for Desktop, Image for Mobile */}
        <div className="absolute inset-0 overflow-hidden">
          {!isMobile ? (
            // Desktop - Video Background
            <video 
              className="absolute inset-0 w-full h-full object-cover z-0"
              autoPlay 
              muted 
              loop 
              playsInline
              preload="auto"
              onLoadStart={() => console.log("Video loading started")}
              onCanPlay={(e) => {
                console.log("Video can play");
                e.target.play().catch(err => console.log("Autoplay prevented:", err));
              }}
              onError={(e) => {
                console.log("Video error:", e);
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_finance-nexus-2/artifacts/ulk4zf0x_853779-hd_1920_1080_25fps.mp4" type="video/mp4" />
            </video>
          ) : (
            // Mobile - Static Image Background
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_finance-nexus-2/artifacts/o5mc5mi8_Image%2031.jpeg')`
              }}
            ></div>
          )}
          {/* Overlay for better logo visibility */}
          <div className="absolute inset-0 bg-black/50 z-10"></div>
        </div>

        {/* Navigation Dropdown - Top Right Corner */}
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-gray-800/80 hover:bg-gray-700/80 backdrop-blur-sm text-white p-2 sm:p-3 rounded-lg border border-gray-600/50 transition-colors duration-300 flex items-center space-x-2"
            >
              <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">Menu</span>
            </button>
            
            {isMenuOpen && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 bg-black bg-opacity-50 z-40"
                  onClick={() => setIsMenuOpen(false)}
                ></div>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 top-full mt-2 w-56 sm:w-64 bg-gray-800/90 backdrop-blur-md rounded-lg border border-gray-600/50 shadow-2xl z-50 max-h-screen overflow-y-auto">
                  <div className="py-2">
                    {[
                      { id: 'about', label: 'Our Approach' },
                      { id: 'services', label: 'Services' },
                      { id: 'investor-network', label: 'Investor Network' },
                      { id: 'financial-healthcheck', label: 'Financial Healthcheck' },
                      { id: 'in-a-nutshell', label: 'In a Nutshell' },
                      { id: 'investment-opportunities', label: 'Opportunities' },
                      { id: 'invest', label: 'Invest' },
                      { id: 'team', label: 'Team' },
                      { id: 'contact', label: 'Contact' }
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          scrollToSection(item.id);
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 sm:px-4 sm:py-3 text-white hover:bg-gray-700/50 hover:text-teal-400 transition-colors duration-300 flex items-center space-x-3"
                      >
                        <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Floating Logo - Centered */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8 md:p-12 relative z-10">
          <div 
            className="text-center cursor-pointer group transition-all duration-500 hover:scale-105"
            onClick={() => scrollToSection('about')}
          >
            {/* Double-sized Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-nexus-2/artifacts/9je566px_noBgColor-2.png" 
                alt="Ingenious Capital - Click to Enter" 
                className="object-contain mx-auto group-hover:drop-shadow-2xl transition-all duration-500"
                style={{
                  width: '820px',
                  height: '820px'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main Site Component
  const MainSite = () => {
    const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
    
    return (
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
            <div className="flex justify-between items-center">
              <button
                onClick={() => navigateToPage('logo')}
                className="text-gray-900 hover:text-teal-600 transition-colors font-medium flex items-center space-x-1 sm:space-x-2"
              >
                <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 rotate-180" />
                <span className="text-xs sm:text-sm">Return to Home</span>
              </button>
              
              {/* Main Navigation Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsMainMenuOpen(!isMainMenuOpen)}
                  className="bg-white hover:bg-gray-50 text-gray-900 px-3 py-2 sm:px-4 sm:py-2 rounded-lg border border-gray-200 transition-colors duration-300 flex items-center space-x-1 sm:space-x-2"
                >
                  <Menu className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Navigation</span>
                  <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-300 ${isMainMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isMainMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 bg-black bg-opacity-50 z-40"
                      onClick={() => setIsMainMenuOpen(false)}
                    ></div>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 top-full mt-2 w-56 sm:w-64 bg-gray-800 rounded-lg border border-gray-600 shadow-2xl z-50 max-h-screen overflow-y-auto">
                      <div className="py-2">
                        {[
                          { id: 'about', label: 'Our Approach' },
                          { id: 'services', label: 'Services' },
                          { id: 'investor-network', label: 'Investor Network' },
                          { id: 'financial-healthcheck', label: 'Financial Healthcheck' },
                          { id: 'in-a-nutshell', label: 'In a Nutshell' },
                          { id: 'investment-opportunities', label: 'Opportunities' },
                          { id: 'invest', label: 'Invest' },
                          { id: 'team', label: 'Team' },
                          { id: 'contact', label: 'Contact' }
                        ].map(item => (
                          <button
                            key={item.id}
                            onClick={() => {
                              scrollToSection(item.id);
                              setIsMainMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 sm:px-4 sm:py-3 text-white hover:bg-gray-700 hover:text-teal-400 transition-colors duration-300 flex items-center space-x-3"
                          >
                            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </nav>

      {/* Our Approach Section - IMPROVED VERSION */}
      <section id="about" className="py-20 bg-white relative">
        <div className="max-w-full mx-auto px-2 relative z-10 w-full">
          {/* 1 line spacing from top */}
          <div className="h-4"></div>
          
          <div className="text-center mb-4">
            <p className="text-lg text-gray-800 mb-8 max-w-6xl mx-auto leading-relaxed p-4 border border-gray-200 rounded-2xl">
              As a sophisticated or high net worth investor, it is unlikely that we are the first investment portal that you have visited, and it is with that in mind that we try to do things differently. 
              Our focus is you and how we can help you to declutter your finances and simplify your investments.
            </p>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Our <span className="text-teal-600">Approach</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-5xl mx-auto leading-relaxed">
              To familiarize yourself with non-traditional, alternative investments requires a partner 
              who has a substantial stake in the game themselves to ensure that the decision
              making is in everybody's best interests. Ingenious Capital does exactly that by co-investing
              in every project that it promotes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-6 max-w-6xl mx-auto px-4 scale-85">
            <div className="w-full">
              <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200 min-h-[380px] flex flex-col relative overflow-hidden">
                {/* Clean border accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-teal-500"></div>
                
                <h3 className="text-3xl font-bold text-gray-900 mb-4 flex items-center relative z-10">
                  <div className="bg-teal-500 p-2 rounded-xl mr-3 shadow-lg">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  Enhanced Liquidity
                </h3>
                <p className="text-lg text-gray-800 leading-relaxed mb-3 relative z-10">
                  Enhanced profits come at a price – lack of liquidity. 
                </p>
                <p className="text-xl font-bold text-teal-600 mb-4 relative z-10">
                  UNTIL NOW...
                </p>
                <p className="text-lg text-gray-800 leading-relaxed mb-5 relative z-10">
                  Ingenious Capital invests into a diverse portfolio of assets ranging from commodities, 
                  shares, ETF's and AIM listed Companies to maintain liquidity and create income. We also invest in property 
                  based business opportunities such as hotels, build to rent units, self storage, holiday parks 
                  and more recently, in demand sporting facilities such as Padel Tennis Centres, considering only those with capable and proven operators. We see this as de-risking 
                  property investment, no developer risk (we have witnessed so many investments fail when involved with planning and construction risk). 
                </p>
                
                {/* Reduced spacing */}
                <div className="h-3"></div>
                
                <p className="text-lg font-bold text-gray-900 mb-4">
                  This philosophy means that 50% of your funds invested are available to you on 48 hours notice, 
                  with the other 50% on a maximum of 180 days notice.
                </p>
              </div>
            </div>
            
            <div className="w-full">
              <div className="relative">
                <div className="relative bg-white rounded-2xl p-6 shadow-2xl border border-gray-200 min-h-[380px] flex flex-col overflow-hidden">
                  {/* Clean border accent */}
                  <div className="absolute top-0 right-0 w-full h-1 bg-orange-500"></div>
                  
                  <h4 className="text-3xl font-bold text-gray-900 mb-5 text-center relative z-10">Investment Returns</h4>
                  <p className="text-lg text-gray-800 mb-6 text-center relative z-10">
                    A world with targeted returns of 1.5 to 6 times initial investment and IRR's of 25-30%.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-5 relative z-10">
                    <div className="text-center bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-lg">
                      <div className="text-2xl font-bold text-teal-500 mb-2">1.5-6x</div>
                      <div className="text-gray-800 font-medium text-base">Return Multiple</div>
                      <div className="mt-3 bg-gray-200 rounded-full h-2">
                        <div className="bg-teal-500 h-2 rounded-full w-4/5"></div>
                      </div>
                    </div>
                    <div className="text-center bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-lg">
                      <div className="text-2xl font-bold text-orange-500 mb-2">25-30%</div>
                      <div className="text-gray-800 font-medium text-base">Targeted IRR</div>
                      <div className="mt-2 bg-gray-200 rounded-full h-1">
                        <div className="bg-orange-500 h-1 rounded-full w-3/4"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - WITH YOUR CUSTOM IMAGES */}
      <section id="services" className="py-20 bg-white relative">
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Tailored Investment <span className="text-teal-600">Solutions</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-4xl mx-auto leading-relaxed">
              We will tailor your investment to suit you, with funds being distributed in accordance with your end goals. 
              You may want quarterly income, you may want compound growth, you may require Shariah compliant investing, 
              you may want to make regular contributions rather than a lump sum. We can accommodate all of your needs to 
              ensure that you are comfortable with the way that your funds are invested and the returns that you are achieving.
            </p>
          </div>
          
          {/* Investment Images Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_ingcap-pickup/artifacts/u617b2tn_Image%2027.jpeg"
                alt="Investment Bubbles"
                className="w-full h-48 rounded-xl mb-4 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Market Wisdom</h3>
              <p className="text-base text-gray-800 text-center leading-relaxed">
                "You can spend your energy chasing bubbles, or you can understand the wind that carries them — that's where the real opportunity lies."
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_ingcap-pickup/artifacts/xzkezliu_Image%2028.jpeg"
                alt="British Piggy Bank with Coins"
                className="w-full h-48 rounded-xl mb-4 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Unlock Your Wealth</h3>
              <p className="text-base text-gray-800 text-center leading-relaxed">
                "How many millionaires do you know who have become wealthy by investing in savings accounts? I rest my case." — Robert G. Allen
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_ingcap-pickup/artifacts/7qbympt2_Image%2026.jpeg"
                alt="Highway Interchange Roadmap"
                className="w-full h-48 rounded-xl mb-4 object-cover"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Choose Your Path</h3>
              <p className="text-base text-gray-800 text-center leading-relaxed">
                "There's No Single Route to Wealth — Different Paths Suit Different People."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Opportunities Section */}
      <section id="investment-opportunities" className="py-20 bg-white relative">
        
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Investment Opportunities
            </h2>
            <p className="text-lg text-gray-800 max-w-3xl mx-auto leading-relaxed">
              Carefully selected opportunities for sophisticated investors
            </p>
          </div>

          {/* Investment Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            
            {Object.entries(investmentOpportunities).map(([key, investment]) => (
              <div key={key} className="group cursor-pointer" onClick={() => setSelectedInvestment(investment)}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl border border-gray-200 group hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105 h-full min-h-[350px] flex flex-col">
                  <div className="aspect-w-16 aspect-h-12 bg-gray-100">
                    <img 
                      src={investment.image} 
                      alt={investment.name}
                      className="w-full h-52 object-cover"
                    />
                  </div>
                  <div className="p-8 flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{investment.name}</h3>
                      <p className="text-gray-600 text-lg mb-3">{investment.category}</p>
                      {investment.fundingInfo && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-800">{investment.fundingInfo}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300 shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Investment Process Section */}
      <section id="invest" className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Investment <span className="text-teal-600">Process</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-3xl mx-auto leading-relaxed">
              We share all of our risk evaluation data with you to show what makes for a good investment.
            </p>
          </div>
          
          {/* Our Selected Co-Investments */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-teal-500 p-2 rounded-2xl mr-3">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Selected Co-Investments</h3>
              </div>
              <div className="space-y-4">
                <p className="text-lg text-gray-800 leading-relaxed">
                  Co-investments are typically made into a specific company or project, with outcomes shaped by (i) the viability of the underlying product or service, and (ii) management's capacity to execute effectively and on time.
                </p>
                <p className="text-lg text-gray-800 leading-relaxed">
                  Our role is to evaluate these factors rigorously and align them with market conditions over the term of the investment. In constructing the portfolio, we aim for a measured balance between near-term opportunities, which enhance liquidity and provide earlier return visibility, and medium-term opportunities, which offer higher growth and profitability potential.
                </p>
                <p className="text-lg text-gray-800 leading-relaxed">
                  This blended approach ensures that capital is working efficiently across different time horizons, mitigating risk while optimising overall return profiles.
                </p>
              </div>
            </div>
          </div>

          {/* Investment Process */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Investment Process</h4>
                <div className="space-y-3">
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'due-diligence' ? null : 'due-diligence')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">The Due Diligence Process</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'due-diligence' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'due-diligence' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['due-diligence'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['due-diligence'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'share-price' ? null : 'share-price')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">How is the share price calculated?</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'share-price' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'share-price' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['share-price'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['share-price'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'company-valuation' ? null : 'company-valuation')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">How is the valuation of a company calculated?</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'company-valuation' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'company-valuation' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['company-valuation'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['company-valuation'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'making-investment' ? null : 'making-investment')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">Making Your Investment</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'making-investment' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'making-investment' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['making-investment'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['making-investment'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'after-investment' ? null : 'after-investment')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">What happens after you've invested?</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'after-investment' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'after-investment' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['after-investment'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['after-investment'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setExpandedInvestmentSection(expandedInvestmentSection === 'investment-returns' ? null : 'investment-returns')}
                    className="w-full text-left p-6 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-[80px] flex items-center"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-lg font-semibold text-gray-900">Making a return on your investment</span>
                      <ArrowRight className={`h-5 w-5 text-gray-600 transition-transform ${expandedInvestmentSection === 'investment-returns' ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedInvestmentSection === 'investment-returns' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-2">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">{investmentProcessContent['investment-returns'].title}</h4>
                      <div className="text-gray-800 leading-relaxed space-y-4 text-base">
                        {investmentProcessContent['investment-returns'].content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className={paragraph.includes(':') && !paragraph.includes('?') ? 'font-medium' : ''}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Expert Investment <span className="text-teal-600">Team</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-3xl mx-auto leading-relaxed">
              Our experienced team brings together deep expertise in venture capital, private equity, 
              and structured finance to deliver exceptional results for our investors.
            </p>
          </div>
          
          {/* Team Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center bg-white rounded-3xl p-6 shadow-xl border border-gray-200">
              <div className="bg-teal-500 p-3 rounded-full w-fit mx-auto mb-3">
                <Award className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">75+</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Years Combined Experience</h3>
              <p className="text-base text-gray-800">Venture capital, private equity, and structured finance</p>
            </div>
            
            <div className="text-center bg-white rounded-3xl p-6 shadow-xl border border-gray-200">
              <div className="bg-orange-500 p-3 rounded-full w-fit mx-auto mb-3">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">£200M+</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Capital Deployed</h3>
              <p className="text-base text-gray-800">Across 100+ early-stage and growth deals</p>
            </div>
            
            <div className="text-center bg-white rounded-3xl p-6 shadow-xl border border-gray-200">
              <div className="bg-gray-500 p-3 rounded-full w-fit mx-auto mb-3">
                <Target className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">Multiple</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Successful Exits</h3>
              <p className="text-base text-gray-800">To major acquirers and strategic buyers</p>
            </div>
            
            <div className="text-center bg-white rounded-3xl p-6 shadow-xl border border-gray-200">
              <div className="bg-teal-600 p-3 rounded-full w-fit mx-auto mb-3">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">Diverse</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Sector Expertise</h3>
              <p className="text-base text-gray-800">AI, biotech, fintech, stock analysis & property</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Investor Network Section - IMPROVED VERSION */}
      <section id="investor-network" className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Our Investor <span className="text-teal-600">Network</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mb-6">
              As we develop our client base we will be looking to cap it at no more than 500, to ensure that we are able to give a complete and personalised service to all of our customers. With that in mind when you make your first investment you will be allocated a membership number to ensure that you do not miss out on any of the upcoming opportunities.
            </p>
          </div>
          
          {/* Investor Network Benefits */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl p-6 shadow-2xl border border-gray-200 scale-95">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Investor Network Benefits</h3>
              
              {/* Investor Network Benefits - Complete List Format */}
              <div className="space-y-12">
                
                {/* Exclusive Deal Flow */}
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Exclusive Deal Flow</h4>
                  <p className="text-base text-gray-800 leading-relaxed">
                    Access to pre-seed and mezzanine opportunities before they reach the market
                  </p>
                </div>
                
                {/* Quarterly Reporting */}
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Quarterly Reporting</h4>
                  <p className="text-base text-gray-800 leading-relaxed">
                    Transparent performance updates and portfolio company progress
                  </p>
                </div>
                
                {/* Co-Investment */}
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Co-Investment</h4>
                  <p className="text-base text-gray-800 leading-relaxed">
                    Opportunities for direct investment alongside the fund
                  </p>
                </div>
                
                {/* Investor Events */}
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Investor Events</h4>
                  <p className="text-base text-gray-800 leading-relaxed">
                    Regular networking events and portfolio company presentations
                  </p>
                </div>
                
                {/* Discounted Partner Services */}
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Discounted Partner Services</h4>
                  <p className="text-base text-gray-800 leading-relaxed mb-6">
                    Discounted pricing on all our partner services including:
                  </p>
                  <div className="space-y-2 ml-6">
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Accounting & Tax positioning</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Legal services</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Will Writing</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Discretionary Trust set up</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Pension advice</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Mortgage reviews</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-teal-500 mt-2">•</span>
                      <span className="text-base text-gray-800">Investment Appraisals</span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Membership highlight */}
              <div className="mt-8 text-center bg-white rounded-2xl p-6 border border-gray-200 shadow-xl">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Limited Membership</h4>
                <div className="flex justify-center items-center space-x-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-teal-500 mb-1">500</div>
                    <div className="text-gray-600 font-medium text-sm">Maximum Members</div>
                  </div>
                  <div className="w-px h-12 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-500 mb-1">#1</div>
                    <div className="text-gray-600 font-medium text-sm">Your Membership Number</div>
                  </div>
                </div>
                <p className="text-gray-900 font-medium mt-3 text-base bg-gray-50 rounded-lg p-3 border border-gray-200">
                  Exclusive membership ensures personalized service and priority access to opportunities
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Financial Healthcheck Section */}
      <section id="financial-healthcheck" className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Free Financial <span className="text-teal-600">Healthcheck</span>
            </h2>
          </div>
          
          <div className="relative scale-90">
            <div className="relative bg-white rounded-2xl p-6 shadow-2xl border border-gray-200">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-gray-800 leading-relaxed text-center mb-12">
                  We want to be the investment partner to guide you through the financial maze. Not only are we committed to the projects that we invest in, but alongside our partners at Way Fund Managers we carry out a free assessment of your current portfolios, identifying areas of risk.
                </p>
                
                {/* Complete List Format */}
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">1. Annual Mortgage Review</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Ensure you're not paying more than you should be
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">2. Professional Will Writing</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Protect yours and your family's wealth
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">3. Pension Review</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Multiple pensions or retirement drawdown optimization
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">4. Insurance Review</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Protection from employment loss, illness or death
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">5. Tax Optimization</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Maximize various tax incentives for optimum returns
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Secondary Market</h3>
                    <p className="text-base text-gray-800 leading-relaxed">
                      Offload underperforming investments
                    </p>
                  </div>
                  
                  <div className="text-center pt-6 border-t border-gray-200">
                    <p className="text-base text-gray-800 leading-relaxed">
                      All services provided by FCA regulated partners • No affiliate fees • Maximum discounts negotiated for you
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* In a Nutshell Section - ENHANCED */}
      <section id="in-a-nutshell" className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              In a <span className="text-teal-600">Nutshell</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-3xl mx-auto leading-relaxed">
              Everything you need to know about partnering with Ingenious Capital - your comprehensive investment solution.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Complete "In a Nutshell" List */}
            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Co investing with experienced partners to ensure full due diligence and risk profiling
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Investment opportunities previously available only to institutional investors
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Continuous liquidity with 50% of invested funds available on 48 hours notice and the balance within 180 days with no forfeiture of profit
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  The opportunity to tidy up your personal finances and create a one stop shop for all your finances
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Tax efficiency assured on both new and existing investments
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  The opportunity to exit underperforming investments via our Secondary market
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Earn while you learn by following our regular tutorials and seminars
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Benefit from up to 25% cost savings by using our recommended partners
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Choose how you get paid: Quarterly Income, Annual Roll Up or a mixture of both
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  Shariah compliant Investing
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  No initial or ongoing management fees
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-teal-500 mt-1 flex-shrink-0" />
                <p className="text-base text-gray-800 leading-relaxed">
                  All forms of payment accepted: Any currency, credit and debit cards, all payment gateways, and a wide range of cryptocurrencies
                </p>
              </div>
              
            </div>
          </div>



          {/* Fee Structure Highlight */}
          <div className="relative scale-90">
            <div className="relative bg-white rounded-2xl p-8 shadow-2xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Fee Structure</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-teal-500 mb-2">0%</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Initial & Management Fees</h4>
                    <p className="text-gray-800 text-base">No upfront costs or ongoing management fees</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-500 mb-2">15%+</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Minimum Return Threshold</h4>
                    <p className="text-gray-800 text-base">We only earn when you earn above 15% annual return</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">Profit Sharing Above 15% Annual Return</h4>
                <div className="flex justify-center items-center space-x-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-500 mb-2">30%</div>
                    <div className="text-gray-800 font-medium text-base">Your Share</div>
                  </div>
                  <div className="w-px h-12 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-500 mb-2">5%</div>
                    <div className="text-gray-800 font-medium text-base">Our Share</div>
                  </div>
                </div>
                <p className="text-gray-800 text-center text-base mt-3">
                  *Example: If we make you a 35% return in a year, we will charge 25% of everything over 15% which in this case is 20%, meaning our share of profit will be 5% yours will be 30%.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - UPDATED */}
      <section id="contact" className="py-20 bg-white relative">
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Get In <span className="text-teal-600">Touch</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your investment portfolio? Contact our expert team today to schedule your consultation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-teal-500 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Phone</h3>
                    <p className="text-gray-800">020 3916 5288</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-orange-500 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Email</h3>
                    <p className="text-gray-800">investor@ingcap.co.uk</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gray-500 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Address</h3>
                    <p className="text-gray-800">1 Canada Square, Canary Wharf<br />London E14 5AA</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Call to Action - UPDATED */}
            <div className="text-center lg:text-left">
              <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Want to know more ?</h3>
                
                <div className="space-y-3">
                  <button
                    onClick={() => setShowBookingCalendar(true)}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 px-8 rounded-xl font-medium transition-all duration-500 hover:scale-105 text-center"
                  >
                    Schedule a Consultation
                    <Calendar className="ml-2 h-5 w-5 inline" />
                  </button>
                  
                  <a
                    href="mailto:investor@ingcap.co.uk"
                    className="inline-block w-full bg-gray-500 hover:bg-gray-600 text-white py-4 px-8 rounded-xl font-medium transition-all duration-500 hover:scale-105 text-center"
                  >
                    Email Us
                    <Mail className="ml-2 h-5 w-5 inline" />
                  </a>
                </div>
                
                <p className="text-base text-gray-800 text-center mt-4">
                  Schedule a consultation or drop us a line with your enquiry
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sitemap Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 relative">
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Ingenious<br/>
                  <span className="text-teal-600">CAPITAL</span>
                </h3>
                <p className="text-gray-800 text-base leading-relaxed">
                  An investment company offering opportunities exclusively to High Net Worth Individuals (HNWIs) and Sophisticated Investors as defined under the Financial Services and Markets Act 2000.
                </p>
              </div>
            </div>
            
            {/* Quick Links */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Our Approach
                </button>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Services
                </button>
                <button
                  onClick={() => scrollToSection('investor-network')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Investor Network
                </button>
                <button
                  onClick={() => scrollToSection('financial-healthcheck')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Financial Healthcheck
                </button>
                <button
                  onClick={() => scrollToSection('in-a-nutshell')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  In a Nutshell
                </button>
                <button
                  onClick={() => scrollToSection('investment-opportunities')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Opportunities
                </button>
                <button
                  onClick={() => scrollToSection('invest')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Invest
                </button>
                <button
                  onClick={() => scrollToSection('team')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Team
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-800 hover:text-teal-600 transition-colors text-base text-left col-span-2"
                >
                  Contact
                </button>
              </div>
            </div>
            
            {/* Legal Links */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Legal</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowPrivacyNotice(true)}
                  className="block text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Privacy Notice
                </button>
                <button 
                  onClick={() => setShowTermsOfUse(true)}
                  className="block text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Terms of Use
                </button>
                <button 
                  onClick={() => setShowRiskDisclosure(true)}
                  className="block text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Risk Disclosure
                </button>
                <button 
                  onClick={() => setShowRegulatoryInfo(true)}
                  className="block text-gray-800 hover:text-teal-600 transition-colors text-base text-left"
                >
                  Regulatory Information
                </button>
              </div>
            </div>
          </div>
          
          {/* Copyright Notice */}
          <div className="border-t border-gray-300 pt-6">
            <div className="text-center">
              <p className="text-gray-800 text-base leading-relaxed">
                © 2025 Ingenious Capital Ltd. All rights reserved.<br/>
                Ingenious Capital Limited is not authorised or regulated by the Financial Conduct Authority (FCA). 
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Regulatory Information Modal */}
      <RegulatoryInformationModal
        showRegulatoryInfo={showRegulatoryInfo}
        setShowRegulatoryInfo={setShowRegulatoryInfo}
      />

      {/* Risk Disclosure Modal */}
      <RiskDisclosureModal
        showRiskDisclosure={showRiskDisclosure}
        setShowRiskDisclosure={setShowRiskDisclosure}
      />

      {/* Terms of Use Modal */}
      <TermsOfUseModal
        showTermsOfUse={showTermsOfUse}
        setShowTermsOfUse={setShowTermsOfUse}
      />

      {/* Privacy Notice Modal */}
      <PrivacyNoticeModal
        showPrivacyNotice={showPrivacyNotice}
        setShowPrivacyNotice={setShowPrivacyNotice}
      />

      {/* Investment Detail Modal */}
      <InvestmentDetailModal
        investment={selectedInvestment}
        onClose={() => setSelectedInvestment(null)}
      />

      {/* Booking Calendar Overlay */}
      <BookingCalendar
        showBookingCalendar={showBookingCalendar}
        setShowBookingCalendar={setShowBookingCalendar}
      />
      </div>
    );
  };

  if (currentPage === 'logo') {
    return <LogoPage />;
  }

  return <MainSite />;
};

export default IngeniousCapital;